import _ from 'lodash';
import { publish } from '@jutro/events';
import { ADOBE_TOPICS } from 'sg-adobe-analytics';
import SgSptConstants from 'sg-spt-constants';
import {
    SgSptTripUtils, SgSubmissionUtil, CommonAdobeAnalyticsUtil, SgProductService
} from 'saga-modules';
import { SG_AA_COVER_LEVELS } from '../AdobeAnalyticsConstants';

export default class SptAdobeAnalyticsUtil {
    static formatSingleTripDestinationValue(value) {
        if (value.length === 0) {
            return undefined;
        }
        let formatedValue = '';
        _.each(value, (destination) => {
            const countryName = CommonAdobeAnalyticsUtil.getTypelistValue(
                'SPTDestination', destination.destination
            );
            formatedValue += `${countryName}|`;
        });
        formatedValue = formatedValue.slice(0, -1);
        return formatedValue;
    }

    static formatActivityTypeValue(value) {
        if (value.length === 0) {
            return undefined;
        }
        let formatedValue = '';
        _.each(value, (activityType) => {
            const activityName = CommonAdobeAnalyticsUtil.getTypelistValue(
                'SPTTripType', activityType.tripType
            );
            formatedValue += `${activityName}|`;
        });
        formatedValue = formatedValue.slice(0, -1);
        return formatedValue;
    }

    static formatAdditionalTravellerTitleValue(value) {
        if (value.length <= 1) {
            return undefined;
        }
        let formatedValue = '';
        _.each(value, (traveller) => {
            if (traveller.isPolicyHolder === false && traveller.person.prefix) {
                formatedValue += `${CommonAdobeAnalyticsUtil.getTypelistValue('NamePrefix', traveller.person.prefix)}|`;
            }
        });
        formatedValue = formatedValue.slice(0, -1);
        return formatedValue;
    }

    static transformCoverTypeValue(value) {
        if (!value || !value.code) {
            return undefined;
        }
        if (value.code === SgSptConstants.PRODUCT_TYPE.SINGLE_TRIP) {
            publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: 'numberOfDaysTillCoverStart', value: 0 });
            return 'Single (One trip)';
        }
        publish(ADOBE_TOPICS.ADOBE_EVENT, { adobeID: 'duration', value: '365' });
        return 'Annual (Multi-trip)';
    }

    static formatWinterSportsValue(value) {
        const policyHolder = _.find(value, { isPolicyHolder: true });
        if (policyHolder.isWinterSportsNeeded === undefined) {
            return undefined;
        }
        let formatedValue = '';
        _.each(value, (traveller, index) => {
            if (traveller.isWinterSportsNeeded) {
                formatedValue += `traveller${index + 1}|`;
            }
        });
        if (formatedValue === '') {
            return 'no';
        }
        formatedValue = formatedValue.slice(0, -1);
        return formatedValue;
    }

    static formatRegionValue(value) {
        if (!value) {
            return undefined;
        }
        const code = value.code || value;
        switch (code) {
            case SgSptConstants.REGIONS.EUROPE:
                return SgSptConstants.ADOBE_REGION_VALUES.EUROPE;
            case SgSptConstants.REGIONS.WORLDWIDE_EXCL:
                return SgSptConstants.ADOBE_REGION_VALUES.WORLDWIDE_EXCL;
            case SgSptConstants.REGIONS.WORLDWIDE_INCL:
                return SgSptConstants.ADOBE_REGION_VALUES.WORLDWIDE_INCL;
            default:
                return undefined;
        }
    }

    static formatSelectedCoverLevelPremium(modelVM, isAnnualTag) {
        if (isAnnualTag !== SgSptTripUtils.isSelectedTripAnnual(modelVM) || !modelVM.quoteData.offeredQuotes) {
            return undefined;
        }
        if (SgSubmissionUtil.isAggregatorQuote(modelVM)) {
            return SgSubmissionUtil.getAnnualPremium(modelVM);
        }
        if (!SgSptTripUtils.isCoverLevelSelected(modelVM)) {
            return undefined;
        }
        if (SgSubmissionUtil.isBound(modelVM)) {
            return undefined;
        }
        const selectedCoverLevel = SgSptTripUtils.getSelectedTrip(modelVM).coverLevel;
        const premiums = SgSubmissionUtil.getSelectedPaymentPlan(modelVM).lobData.privateTravel;
        const selectedPrice = premiums[`${selectedCoverLevel}Amount`];
        return `${selectedPrice.toFixed(2)}`;
    }

    static formatPriceDifferences(modelVM) {
        if (!SgSptTripUtils.isCoverLevelSelected(modelVM) || !modelVM.quoteData.offeredQuotes || SgSubmissionUtil.isBound(modelVM)) {
            return undefined;
        }
        const selectedTrip = SgSptTripUtils.getSelectedTrip(modelVM);
        const premiums = SgSubmissionUtil.getSelectedPaymentPlan(modelVM).lobData.privateTravel;
        const selectedCoverLevel = selectedTrip.coverLevel;
        const selectedPremium = premiums[`${selectedCoverLevel}Amount`];
        const allCoverLevels = Object.values(SgSptConstants.COVER_LEVELS);
        const unselectedCoverLevels = allCoverLevels.filter((coverLevel) => coverLevel !== selectedCoverLevel);
        let unselectedPremiums = unselectedCoverLevels.map((coverLevel) => {
            return premiums[`${coverLevel}Amount`];
        });
        unselectedPremiums = unselectedPremiums.filter((premium) => premium !== undefined);
        const diffSelectedAndMaxPrices = selectedPremium - Math.max(...unselectedPremiums);
        const diffSelectedAndMinPrices = selectedPremium - Math.min(...unselectedPremiums);
        return `${diffSelectedAndMaxPrices.toFixed(2)}|${diffSelectedAndMinPrices.toFixed(2)}`;
    }

    static formatProduct(modelVM) {
        let productName = `${SgProductService.getInsuranceProductName()} - `;
        if (!SgSptTripUtils.isCoverLevelSelected(modelVM)) {
            return undefined;
        }
        const selectedTrip = SgSptTripUtils.getSelectedTrip(modelVM);
        switch (selectedTrip.coverLevel) {
            case SgSptConstants.COVER_LEVELS.STANDARD:
                productName += SG_AA_COVER_LEVELS.STANDARD.toLowerCase();
                break;
            case SgSptConstants.COVER_LEVELS.SELECT:
                productName += SG_AA_COVER_LEVELS.SELECT.toLowerCase();
                break;
            case SgSptConstants.COVER_LEVELS.PLUS:
                productName += SG_AA_COVER_LEVELS.PLUS.toLowerCase();
                break;
            case SgSptConstants.COVER_LEVELS.ESSENTIAL:
                productName += SG_AA_COVER_LEVELS.ESSENTIAL.toLowerCase();
                break;
            default:
                productName += SG_AA_COVER_LEVELS.UNKNOWN.toLowerCase();
        }
        return productName;
    }

    static trackUpgradeOfferedValue(modelVM) {
        if (!SgSptTripUtils.isSelectedTripSingle(modelVM) || !SgSubmissionUtil.isQuoted(modelVM)) {
            return undefined;
        }
        return !SgSubmissionUtil.isAggregatorQuote(modelVM) && modelVM.value.lobData.privateTravel.coverables.sptTrips.length > 1;
    }
}
